import React from 'react';

import * as ModalContainer from 'ui-library/ModalContainer';
import {MakeFieldsOptional} from 'util/Types';

import ChooseImportLocationModalPane from './panes/ChooseImportLocationModalPane';
import {configureMapColumnNamesModalPane} from './panes/MapColumnNamesModalPane';
import ShowErrorsModalPane from './panes/ShowErrorsModalPane';
import UploadSpreadsheetModalPane from './panes/UploadSpreadsheetModalPane';
import * as SpreadsheetBulkImportActions from './SpreadsheetBulkImportActions';
import {
  COLUMN_TYPE,
  IMPORT_TYPE,
  Pane,
  PaneKey,
  SelectedSchemaIds,
  SPREADSHEET_BULK_IMPORT_KEY,
  SpreadsheetColumn,
  UPDATE_MIXTURE_INGREDIENT_COLUMN_TYPES,
} from './SpreadsheetBulkImportConstants';
import SpreadsheetBulkImportModalContainer from './SpreadsheetBulkImportModalContainer';
import * as SpreadsheetBulkImportUtil from './SpreadsheetBulkImportUtil';

function getPanes(): Array<Pane> {
  return [
    {
      paneKey: PaneKey.SET_IMPORT_SETTINGS_FOR_DESTINATION,
      configuration: ChooseImportLocationModalPane.configure({
        showRegistries: true,
        showInventory: false,
        shouldShowSchemaSelector: false,
        folderSelectorLabel: 'Import location',
        shouldFilterToComplexPolymerSchemas: false,
      }),
    },
    {
      paneKey: PaneKey.UPLOAD_SPREADSHEET,
      configuration: UploadSpreadsheetModalPane.configure(),
    },
    {
      paneKey: PaneKey.MAP_COLUMN_NAMES,
      configuration: configureMapColumnNamesModalPane({
        onValidateSpreadsheet: SpreadsheetBulkImportActions.validateSpreadsheetImportForMixtureIngredients,
        getValidColumnTypes: () => [...UPDATE_MIXTURE_INGREDIENT_COLUMN_TYPES, COLUMN_TYPE.IGNORE],
        getRequiredColumnsError: (
          columns: Array<SpreadsheetColumn>,
          {selectedSchemaIds}: {selectedSchemaIds: SelectedSchemaIds}
        ) => {
          // require mixture and component columns for id-ing ingredient models
          const requiredColumnError = SpreadsheetBulkImportUtil.getRequiredFieldError(
            [COLUMN_TYPE.INGREDIENT_MIXTURE_LABEL, COLUMN_TYPE.INGREDIENT_COMPONENT_BIOENTITY_LABEL],
            columns,
            selectedSchemaIds,
            IMPORT_TYPE.UPDATE_MIXTURE_INGREDIENTS
          );
          if (requiredColumnError) {
            return requiredColumnError;
          }
          return null;
        },
        linkItemsFolderSelectorLabel: 'Look for component entities and component lots in:',
        linkItemsFolderSelectorDescription:
          'Choose any folders that we should search in to find the component entities and component lots ' +
          'specified in your spreadsheet. We automatically consider all existing component entities and ' +
          'lots in the specified mixtures, and all entities contained in the specified containers.',
      }),
    },
    {
      paneKey: PaneKey.SHOW_ERRORS,
      configuration: ShowErrorsModalPane.configure({
        enableReplaceLinkWithText: false, // no link fields for ingredients
        enableSkipErrors: true,
      }),
    },
  ];
}

export interface BulkImportMixtureIngredientsFlowProps {
  initialFolder: {folderId: string; registryId: string | null} | null;
  onRequestClose(): void;
}

function BulkImportMixtureIngredientsFlow(props: BulkImportMixtureIngredientsFlowProps): JSX.Element {
  return (
    <SpreadsheetBulkImportModalContainer
      importItemWord="mixture's ingredient"
      importType={IMPORT_TYPE.UPDATE_MIXTURE_INGREDIENTS}
      initialSettings={{initialFolder: props.initialFolder}}
      onImportFromSpreadsheet={
        SpreadsheetBulkImportActions.importMixtureIngredientsFromSpreadsheetWithDataMods
      }
      onRequestClose={() => {
        props.onRequestClose();
      }}
      panes={getPanes()}
      title="Bulk update mixture ingredients"
    />
  );
}

export function openBulkImportMixtureIngredientsFlow(
  props: MakeFieldsOptional<BulkImportMixtureIngredientsFlowProps, 'onRequestClose'>
): void {
  ModalContainer.open(
    SPREADSHEET_BULK_IMPORT_KEY,
    <BulkImportMixtureIngredientsFlow {...props} onRequestClose={SpreadsheetBulkImportActions.closeModal} />
  );
}

export default BulkImportMixtureIngredientsFlow;
